import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { Lead } from "model/lead";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { styles } from "./styles";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { getLeadsOptimized } from "redux/selector";
import { getCampaignsSettings } from "redux/selector";
import { updateCampaignsSettings } from "redux/slice/campaigns-settings";
import { CampaignsSettings } from "model/campaigns-settings";
import { LoadingIndicator } from "component/shared/loading-indicator";
import * as env from "shared/env";
import { AppCard } from "component/shared/app-card";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {
}

const Component: React.FC<Props> = ({ user, classes = {} }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const leads = useSelector(getLeadsOptimized);
	const campaignsSettings = useSelector(getCampaignsSettings);

	// Local state
	const [edited, setEdited] = useState(false);
	const [address, setAddress] = useState("");
	const [leadId, setLeadId] = useState(0);
	const [input, setInput] = useState("");

	// Memoized function to get lead display text
	const getLeadDisplay = useCallback((lead: Lead | undefined) => {
		let displayText: string = "";
		if (lead) {
			if (!lead.name) {
				displayText = lead.label;
			} else {
				displayText = lead.email + " (" + lead.name + ")"
			}
		}
		return displayText;
	}, []);

	// Memoized function to get selected lead
	const getSelectedLead = useCallback(() => {
		if (leadId) {
			return leads.find((lead) => lead.id === leadId);
		}
	}, [leads, leadId]);

	// Memoized options for autocomplete
	const options = useMemo(() => {
		return leads.map(lead => ({
			id: lead.id,
			email: lead.email,
			label: getLeadDisplay(lead),
		}));
	}, [leads, getLeadDisplay]);

	// Update state when campaignsSettings changes
	const updateState = useCallback(() => {
		if (campaignsSettings) {
			setAddress(campaignsSettings.dynamicContentAddress || "");
			setLeadId(campaignsSettings.lead ? campaignsSettings.lead.id : 0);
			setInput(getLeadDisplay(campaignsSettings.lead));
		}
	}, [campaignsSettings, getLeadDisplay]);

	// Effect to update state when campaignsSettings changes
	useEffect(() => {
		updateState();
	}, [updateState]);

	const handleSave = useCallback(() => {
		const campaignsSettingsData: CampaignsSettings = {
			dynamicContentAddress: address,
			lead: getSelectedLead(),
		};
		dispatch(updateCampaignsSettings({campaignsSettings: campaignsSettingsData}));
		setEdited(false);
	}, [address, getSelectedLead, dispatch]);

	const handleAddressChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setAddress(event.target.value);
		setEdited(true);
	}, []);

	const handleInputChange = useCallback((event: any, value: string, reason: any) => {
		setInput(value);
	}, []);

	const handleAutocompleteChange = useCallback((event: any, option: any) => {
		if (option && option.label) {
			setLeadId(option.id as number);
			setInput(option.label);
			setEdited(true);
		}
	}, []);

	const title = "Campaign Settings";

	return (
		<DashboardLayout
			permitted={user && user.permissions.campaigns}
			title={title}
			fullScreen={false}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Send />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
		>
			<Mui.Grid container direction="column" spacing={4}>
				{!campaignsSettings ? (
					<Mui.Grid item className={classes.loading}>
						<LoadingIndicator enable={true} />
					</Mui.Grid>
				) : (
					<>
						<Mui.Grid item>
							<AppCard><Mui.CardContent>
									<Mui.Grid container direction="column" spacing={2}>
										<Mui.Grid item>
											<Mui.TextField
												id="address"
												label="Smart Content Default Address"
												placeholder="Enter Address"
												InputLabelProps={{ shrink: true }}
												value={address}
												onChange={handleAddressChange}
												fullWidth
												margin="dense"
												variant="outlined"
											/>
										</Mui.Grid>
										<Mui.Grid item>
											<CustomAutocomplete
												options={options}
												textInputHeight={true}
												label="Select an email for sending test messages"
												optionLabelExtractor={(option) => option.label}
												fullWidth
												margin="dense"
												placeholder="Search for a lead here"
												onInputChange={handleInputChange}
												onChange={handleAutocompleteChange}
												inputValue={input}
											/>
										</Mui.Grid>
									</Mui.Grid>
							</Mui.CardContent></AppCard>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button
								disabled={!edited}
								variant="contained"
								color={"primary"}
								onClick={handleSave}
							>
								{edited ? "Save" : "Saved"}
							</Mui.Button>
						</Mui.Grid>
					</>
				)}
			</Mui.Grid>
		</DashboardLayout>
	);
};

export const CampaignSettingsPage = withStyles(Component, styles);